import { useState } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Link } from "react-router-dom";
import { Send, Search, MoreVertical, Phone, Video, Paperclip, Smile } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { BackLink } from "@/components/layout/BackLink";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const conversations = [
  {
    id: "c1",
    name: "Dr. Sarah Mitchell",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100",
    lastMessage: "That sounds great! Let me know when you're available for our first session.",
    timestamp: "2 min ago",
    unread: 2,
    online: true,
  },
  {
    id: "c2",
    name: "Michael Chen",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100",
    lastMessage: "I have some availability this week if you'd like to schedule a trial lesson!",
    timestamp: "1 hour ago",
    unread: 1,
    online: false,
  },
  {
    id: "c3",
    name: "Emma Rodriguez",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100",
    lastMessage: "¡Hola! I received your message about conversational Spanish.",
    timestamp: "Yesterday",
    unread: 0,
    online: true,
  },
  {
    id: "c4",
    name: "James Thompson",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100",
    lastMessage: "Looking forward to helping you with chemistry!",
    timestamp: "2 days ago",
    unread: 0,
    online: false,
  },
];

const messages = [
  {
    id: "m1",
    senderId: "user",
    content: "Hi Dr. Mitchell! I saw your profile and I'm really interested in getting help with calculus for my SAT prep.",
    timestamp: "10:30 AM",
  },
  {
    id: "m2",
    senderId: "other",
    content: "Hello! Thank you for reaching out. I'd be happy to help you with SAT calculus prep. What specific areas are you struggling with?",
    timestamp: "10:32 AM",
  },
  {
    id: "m3",
    senderId: "user",
    content: "I'm having trouble with derivatives and integration. I understand the basic concepts but I struggle with more complex problems.",
    timestamp: "10:35 AM",
  },
  {
    id: "m4",
    senderId: "other",
    content: "That's very common! Many students find those topics challenging at first. I have a step-by-step approach that has helped many of my students master these concepts. Would you like to schedule a trial session to see if my teaching style works for you?",
    timestamp: "10:38 AM",
  },
  {
    id: "m5",
    senderId: "user",
    content: "Yes, that would be great! I'm available most afternoons after 4pm.",
    timestamp: "10:40 AM",
  },
  {
    id: "m6",
    senderId: "other",
    content: "That sounds great! Let me know when you're available for our first session.",
    timestamp: "10:42 AM",
  },
];

const quickReplies = [
  "Thank you! When are you available?",
  "Yes, I'm interested in a trial session",
  "What's your cancellation policy?",
  "Can we do online lessons?",
];

export default function MessagesPage() {
  usePageTitle('Messages | BookMyCoaching');
  const [selectedConversation, setSelectedConversation] = useState(conversations[0]);
  const [newMessage, setNewMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showMobileChat, setShowMobileChat] = useState(false);

  const filteredConversations = conversations.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;
    // In a real app, this would send the message
    setNewMessage("");
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-20 h-[calc(100vh-80px)]">
        <div className="container mx-auto px-4 h-full py-4">
          <div className="bg-card rounded-2xl border border-border shadow-soft h-full flex overflow-hidden">
            {/* Conversations List */}
            <div className={cn(
              "w-full md:w-96 border-r border-border flex flex-col",
              showMobileChat && "hidden md:flex"
            )}>
              {/* Header */}
              <div className="p-4 border-b border-border">
                <h1 className="font-display text-xl font-bold text-foreground mb-4">Messages</h1>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search conversations..."
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Conversation List */}
              <div className="flex-1 overflow-y-auto">
                {filteredConversations.map(conversation => (
                  <button
                    key={conversation.id}
                    onClick={() => {
                      setSelectedConversation(conversation);
                      setShowMobileChat(true);
                    }}
                    className={cn(
                      "w-full flex items-start gap-3 p-4 hover:bg-muted/50 transition-colors text-left",
                      selectedConversation.id === conversation.id && "bg-muted"
                    )}
                  >
                    <div className="relative">
                      <img
                        src={conversation.avatar}
                        alt={conversation.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      {conversation.online && (
                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-success rounded-full border-2 border-card" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-semibold text-foreground truncate">{conversation.name}</p>
                        <span className="text-xs text-muted-foreground">{conversation.timestamp}</span>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">{conversation.lastMessage}</p>
                    </div>
                    {conversation.unread > 0 && (
                      <Badge variant="default" className="ml-2 w-5 h-5 p-0 flex items-center justify-center text-xs">
                        {conversation.unread}
                      </Badge>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Chat Area */}
            <div className={cn(
              "flex-1 flex flex-col",
              !showMobileChat && "hidden md:flex"
            )}>
              {/* Chat Header */}
              <div className="flex items-center justify-between p-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <BackLink onClick={() => setShowMobileChat(false)} className="md:hidden" />
                  <Link to={`/teacher/t1`} className="flex items-center gap-3">
                    <div className="relative">
                      <img
                        src={selectedConversation.avatar}
                        alt={selectedConversation.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      {selectedConversation.online && (
                        <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-success rounded-full border-2 border-card" />
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{selectedConversation.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {selectedConversation.online ? "Online" : "Offline"}
                      </p>
                    </div>
                  </Link>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon">
                    <Phone className="w-5 h-5" />
                  </Button>
                  <Button variant="ghost" size="icon">
                    <Video className="w-5 h-5" />
                  </Button>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map(message => (
                  <div
                    key={message.id}
                    className={cn(
                      "flex",
                      message.senderId === "user" ? "justify-end" : "justify-start"
                    )}
                  >
                    <div
                      className={cn(
                        "max-w-[75%] rounded-2xl px-4 py-3",
                        message.senderId === "user"
                          ? "bg-primary text-primary-foreground rounded-br-md"
                          : "bg-muted text-foreground rounded-bl-md"
                      )}
                    >
                      <p className="text-sm">{message.content}</p>
                      <p className={cn(
                        "text-xs mt-1",
                        message.senderId === "user" ? "text-primary-foreground/70" : "text-muted-foreground"
                      )}>
                        {message.timestamp}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Replies */}
              <div className="px-4 py-2 border-t border-border">
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {quickReplies.map((reply, i) => (
                    <button
                      key={i}
                      onClick={() => setNewMessage(reply)}
                      className="flex-shrink-0 px-3 py-1.5 text-xs bg-muted hover:bg-accent rounded-full text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {reply}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-border">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="icon">
                    <Paperclip className="w-5 h-5" />
                  </Button>
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1"
                    onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                  />
                  <Button variant="ghost" size="icon">
                    <Smile className="w-5 h-5" />
                  </Button>
                  <Button size="icon" onClick={handleSendMessage}>
                    <Send className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
